import React from "react";
import {
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Stack,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useDispatch, useSelector } from "react-redux";
import { changePassword } from "../actions/auth";
import "./UserAccountPage.css";

export default function ChangePassword() {
  const [changePasswordData, setChangePasswordData] = React.useState({
    passwordCurrent: "",
    password: "",
    passwordConfirm: "",
  });
  const [error, setError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [showAlert, setShowAlert] = React.useState(false);
  const [showCurrent, setShowCurrent] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const dispatch = useDispatch();
  const message = useSelector((state) => state);

  function sendData() {
    // basic validations
    if (
      !changePasswordData.passwordCurrent ||
      !changePasswordData.password ||
      !changePasswordData.passwordConfirm
    ) {
      setError("Please fill all required fields.");
      setShowAlert(false);
      return;
    }

    if (changePasswordData.password !== changePasswordData.passwordConfirm) {
      setError("Passwords do not match.");
      setShowAlert(false);
      return;
    }

    const passwordPattern =
      /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[^\w\s]).{8,}$/;
    if (!passwordPattern.test(changePasswordData.password)) {
      setError(
        "Password must have uppercase, lowercase, number, symbol, and be at least 8 characters."
      );
      setShowAlert(false);
      return;
    }

    setError("");
    setIsLoading(true);
    setShowAlert(true);

    dispatch(changePassword(changePasswordData));

    setTimeout(() => {
      setIsLoading(false);
      setChangePasswordData({
        password: "",
        passwordConfirm: "",
        passwordCurrent: "",
      });
    }, 3000);
  }

  return (
    <div className="password-page-root">
      <Container maxWidth="sm" className="password-container">
        <Paper elevation={0} className="password-card">
          {/* Header */}
          <div className="password-header">
            <div>
              <Typography className="password-title">
                Change Password
              </Typography>
              <Typography className="password-subtitle">
                Choose a strong password that you don&apos;t use anywhere else.
                Changing it signs you out of other devices.
              </Typography>
            </div>
          </div>

          {/* Alerts */}
          {showAlert && (
            <Stack sx={{ width: "100%", marginBottom: "0.6rem" }} spacing={1}>
              {message.auth?.changePasswordError && (
                <Alert severity="error" className="password-alert">
                  Error: {message.auth.changePasswordError}
                </Alert>
              )}
              {message.auth?.changePasswordSuccess && (
                <Alert severity="success" className="password-alert">
                  {message.auth.changePasswordSuccess}
                </Alert>
              )}
            </Stack>
          )}

          {/* Form */}
          <div className="password-form">
            {/* Current password */}
            <div className="password-field-block">
              <Typography className="password-label">
                Current Password *
              </Typography>

              <TextField
                required
                fullWidth
                type={showCurrent ? "text" : "password"}
                placeholder="Enter current password"
                autoComplete="current-password"
                value={changePasswordData.passwordCurrent}
                onChange={(e) =>
                  setChangePasswordData({
                    ...changePasswordData,
                    passwordCurrent: e.target.value,
                  })
                }
                size="small"
                variant="outlined"
                className="password-input"
                InputProps={{
                  endAdornment: (
                    <button
                      type="button"
                      className="password-eye"
                      onClick={() => setShowCurrent(!showCurrent)}
                      aria-label={showCurrent ? "Hide password" : "Show password"}
                    >
                      {showCurrent ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </button>
                  ),
                }}
              />
            </div>

            {/* New password */}
            <div className="password-field-block">
              <Typography className="password-label">New Password *</Typography>

              <TextField
                required
                fullWidth
                type={showNew ? "text" : "password"}
                placeholder="Enter new password"
                autoComplete="new-password"
                value={changePasswordData.password}
                onChange={(e) =>
                  setChangePasswordData({
                    ...changePasswordData,
                    password: e.target.value,
                  })
                }
                size="small"
                variant="outlined"
                className="password-input"
                InputProps={{
                  endAdornment: (
                    <button
                      type="button"
                      className="password-eye"
                      onClick={() => setShowNew(!showNew)}
                      aria-label={showNew ? "Hide password" : "Show password"}
                    >
                      {showNew ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </button>
                  ),
                }}
              />
            </div>

            {/* Confirm new password */}
            <div className="password-field-block">
              <Typography className="password-label">
                Confirm New Password *
              </Typography>

              <TextField
                required
                fullWidth
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm new password"
                autoComplete="new-password"
                value={changePasswordData.passwordConfirm}
                onChange={(e) =>
                  setChangePasswordData({
                    ...changePasswordData,
                    passwordConfirm: e.target.value,
                  })
                }
                size="small"
                variant="outlined"
                className="password-input"
                InputProps={{
                  endAdornment: (
                    <button
                      type="button"
                      className="password-eye"
                      onClick={() => setShowConfirm(!showConfirm)}
                      aria-label={showConfirm ? "Hide password" : "Show password"}
                    >
                      {showConfirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </button>
                  ),
                }}
              />
            </div>

            {error && <span className="password-error-text">{error}</span>}

            {/* Password rules */}
            <div className="password-hint">
              <Typography component="span">Password must contain:</Typography>
              <ul>
                <li>At least 8 characters</li>
                <li>Uppercase & lowercase letters</li>
                <li>At least one number</li>
                <li>At least one special character</li>
              </ul>
            </div>
          </div>

          {/* Actions */}
          <div className="password-footer">
            <Button
              onClick={sendData}
              size="small"
              variant="contained"
              className={`password-submit-btn ${isLoading ? "is-loading" : ""}`}
              disabled={isLoading}
            >
              {isLoading ? "Changing..." : "Change password"}
            </Button>
          </div>
        </Paper>
      </Container>
    </div>
  );
}
